export const PORTAL_HOME = '/portal'
export const LOGIN_PATH = '/portal/login'
export const WELCOME_PATH = '/portal/welcome'
export const REGISTER_PATH = '/portal/register'
export const JOIN_PATH = '/portal/join'
export const COURSES_PATH = '/portal/courses'
export const SEAT_REQUESTS_PATH = '/portal/admin/seat-requests'
export const ENQUIRIES_PATH = '/portal/admin/enquiries'
export const CONTACT_PATH = '/contact'
export const ASSIGNMENTS_PATH = '/portal/assignments'

export function coursePath(courseSlug: string) {
  return `${COURSES_PATH}/${courseSlug}`
}

export function previewLessonPath(courseSlug: string, lessonId: string) {
  return `${coursePath(courseSlug)}/lessons/${lessonId}`
}

export function batchPath(batchId: string) {
  return `/portal/batches/${batchId}`
}

export function batchLessonPath(batchId: string, lessonId: string) {
  return `${batchPath(batchId)}/lessons/${lessonId}`
}

export function batchTestPath(batchId: string, testId: string) {
  return `${batchPath(batchId)}/tests/${testId}`
}

export function batchAttemptPath(batchId: string, testId: string, attemptId: string) {
  return `${batchTestPath(batchId, testId)}/attempts/${attemptId}`
}

export function batchMaterialsPath(batchId: string) {
  return `${batchPath(batchId)}/materials`
}

export function resourceDownloadPath(resourceId: string, versionId?: string) {
  return `/portal/resources/${resourceId}/download${versionId ? `?version=${versionId}` : ''}`
}

export const RESOURCES_PATH = '/portal/admin/resources'
export const NEW_RESOURCE_PATH = `${RESOURCES_PATH}/new`

export function resourcePath(resourceId: string) {
  return `${RESOURCES_PATH}/${resourceId}`
}

export function newResourcePath(home?: { version?: string; batch?: string; lesson?: string }) {
  const params = new URLSearchParams()
  if (home?.version) params.set('home', `version:${home.version}`)
  else if (home?.batch) params.set('home', `batch:${home.batch}`)
  if (home?.lesson) params.set('lesson', home.lesson)
  const search = params.toString()
  return search ? `${NEW_RESOURCE_PATH}?${search}` : NEW_RESOURCE_PATH
}

export function libraryPathFor(home: { version?: string; batch?: string }) {
  const params = new URLSearchParams()
  if (home.version) params.set('home', `version:${home.version}`)
  else if (home.batch) params.set('home', `batch:${home.batch}`)
  return `${RESOURCES_PATH}?${params.toString()}`
}

export const TEACH_HOME = '/portal/teach'
export const TEACH_GRADING_PATH = '/portal/teach/grading'
export const TEACH_NOTES_PATH = '/portal/teach/notes'
export const TEACH_REPORTS_PATH = '/portal/teach/reports'

export function teachBatchPath(batchId: string) {
  return `${TEACH_HOME}/batches/${batchId}`
}

export function teachClassesPath(batchId: string) {
  return `${teachBatchPath(batchId)}/classes`
}

export function teachSessionPath(batchId: string, day: string) {
  return `${teachClassesPath(batchId)}/${day}`
}

export function teachAttendancePath(batchId: string, day?: string) {
  return `${teachBatchPath(batchId)}/attendance${day ? `?date=${day}` : ''}`
}

export function teachLessonPath(batchId: string, lessonId: string) {
  return `${teachBatchPath(batchId)}/lessons/${lessonId}`
}

export function teachCoursePath(batchId: string) {
  return `${teachBatchPath(batchId)}/course`
}

export function teachReportPath(batchId: string) {
  return `${teachBatchPath(batchId)}/report`
}

export function teachStudentPath(studentId: string) {
  return `${TEACH_HOME}/students/${studentId}`
}

export function teachMaterialsPath(batchId: string) {
  return `${teachBatchPath(batchId)}/materials`
}

export function teachTestPath(batchId: string, testId: string) {
  return `${teachBatchPath(batchId)}/tests/${testId}`
}

export function teachAttemptPath(attemptId: string) {
  return `/portal/teach/attempts/${attemptId}`
}

export function assignmentPath(assignmentId: string) {
  return `${ASSIGNMENTS_PATH}/${assignmentId}`
}

export function teachAssignmentPath(assignmentId: string) {
  return `/portal/teach/assignments/${assignmentId}`
}

const PORTAL_PATH = /^\/portal(?:[/?#]|$)/
const AUTH_PATH = /^\/portal\/(?:login|logout|register)(?:[/?#]|$)/

export function isPortalPath(pathname: string) {
  return PORTAL_PATH.test(pathname)
}

export function safePortalPath(value: string | null | undefined) {
  if (!value || value.startsWith('//') || value.includes('\\')) return PORTAL_HOME
  if (!isPortalPath(value) || AUTH_PATH.test(value)) return PORTAL_HOME
  return value
}

export function loginPath(next?: string | null) {
  const target = safePortalPath(next)
  return target === PORTAL_HOME ? LOGIN_PATH : `${LOGIN_PATH}?next=${encodeURIComponent(target)}`
}

const LEGACY_STAFF_ROUTES: { pattern: RegExp; destination: (match: RegExpMatchArray) => string }[] =
  [
    {
      pattern: /^\/portal\/admin\/batches\/([^/]+)\/attendance\/?$/,
      destination: (match) => `/portal/teach/batches/${match[1]}/attendance`,
    },
    { pattern: /^\/portal\/admin\/review\/?$/, destination: () => '/portal/teach/grading' },
    { pattern: /^\/portal\/admin\/follow-ups\/?$/, destination: () => '/portal/teach/notes' },
    {
      pattern: /^\/portal\/admin\/students\/([^/]+)\/?$/,
      destination: (match) => `/portal/teach/students/${match[1]}`,
    },
  ]

export function legacyStaffDestination(url: URL) {
  for (const route of LEGACY_STAFF_ROUTES) {
    const match = url.pathname.match(route.pattern)
    if (match) return `${route.destination(match)}${url.search}`
  }
  return null
}
